// Form is based on Formik
// Data validation is based on Yup
// Please, be familiar with article first:
// https://hackernoon.com/react-form-validation-with-formik-and-yup-8b76bda62e10
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import { useRecoilValueLoadable } from 'recoil';
import { MaterialAutocomplete } from '../../../common/formik-material-ui';
import '../../../common/formik-material-ui/commaSeparatedEmails';
import { DefaultActionSkeleton } from '../../components/dropdowns/DefaultAction';

import { green } from '@mui/material/colors';
import { FieldArray } from 'formik';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  table: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
}));

export interface IBusinessSMSNumbersContent {
  numberId: string;
  DID?: string;
  numberid?: string;
  action?: string;
  destinationName?: string;
}

/**
 * Queue teams table and member add
 *
 * @param {Object} props
 * @param {Object} props.id Queue ID
 *
 * @see  https://stackoverflow.com/questions/48901016/how-to-set-change-field-value-from-external-user-action-react-final-form final-form mutators
 */
export const BusinessSMSNumbers = ({ id, formik, initialValues }: any) => {
  const classes = useStyles();
  const [autocomplete, setAutocomplete] = useState<any>([]);
  const businessSMSNumbers = useRecoilValueLoadable(
    makeAPIRequest({
      // stage: 'itn',
      method: 'get',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint:
        endpointEnum.BUSINESS_SMS_NUMBERS +
        `/${formik.values.businessId ?? ''}`,
      payloadJsonString: JSON.stringify({
        businessId: formik.values.businessId ?? '',
      }),
    })
  );

  const removedNumbers = useMemo(() => {
    return (initialValues?.numbers ?? []).filter((elem) => {
      return !(formik.values.numbers ?? []).find(
        (currentElem) => currentElem?.numberId === elem?.numberId
      );
    });
  }, [formik.values.numbers, initialValues]);
  // get businessTeams that do not belong to queue
  const numbersNotInBusinessSMSRule = useRef<any>([]);
  numbersNotInBusinessSMSRule.current = useMemo(() => {
    return businessSMSNumbers.state === 'loading'
      ? numbersNotInBusinessSMSRule.current
      : businessSMSNumbers.contents?.response?.data?.data
          ?.filter((businessNum: any) => {
            return (
              !businessNum?.businessSMSId ||
              (removedNumbers ?? [])?.find(
                (elem) => elem?.numberId === businessNum?.numberId
              )
            );
          })
          .filter(
            (businessNum: any) =>
              !formik.values.numbers?.find(
                (number: IBusinessSMSNumbersContent) =>
                  number.numberId === businessNum.numberId
              )
          )
          ?.sort((a: any, b: any) =>
            a?.DID?.toLowerCase()?.localeCompare(b?.DID?.toLowerCase())
          )
          .map((elem) => {
            return {
              ...elem,
              numberid: elem?.numberId,
            };
          }) ?? [];
  }, [businessSMSNumbers, formik.values.numbers]);
  /**
   * Change field with new value
   * @param name Name of field (if nested then last value is type)
   */
  const handleChangeForAutocomplete = (
    event: ChangeEvent<unknown>,
    newValue: any[]
  ) => {
    // keep autocomplete in sync
    setAutocomplete(newValue);
  };

  const numbersMemo = useMemo(() => {
    const numbersToDisplay = (formik.values?.numbers ?? []).map((elem) => {
      const destinationNumber = (
        businessSMSNumbers.contents?.response?.data?.data ?? []
      ).find((num) => num?.numberId === elem?.numberId);
      const destinationName = destinationNumber?.destinationName ?? '';
      return {
        ...elem,
        destinationName,
      };
    });
    return numbersToDisplay;
  }, [
    formik.values,
    businessSMSNumbers.contents?.response?.data?.data,
    businessSMSNumbers.state,
  ]);

  return (
    <>
      {/* alignItems: 'center', // set native height for button since flex sets height to be the same for all items */}
      <MaterialAutocomplete
        dataPup={'businessSMSNumbersAutocomplete'}
        className={`flex-grow-1 ${classes.field}`}
        type="text"
        label="Numbers"
        size="small"
        variant="outlined"
        options={numbersNotInBusinessSMSRule.current}
        value={autocomplete}
        multiple={true}
        onChange={handleChangeForAutocomplete}
        getOptionLabel={(option) =>
          `${option.DID ?? ''} - ${option?.destinationName ?? ''}` ?? ''
        }
        isOptionEqualToValue={(option, value) =>
          option?.numberId === value?.numberId
        }
        disabled={businessSMSNumbers.state === 'loading'} // in progress or queue loading
      />
      <FieldArray
        name="numbers"
        render={(arrayHelper) => (
          <>
            <Button
              className={classes.field}
              variant="contained"
              color="primary"
              data-pup={`add-business-sms-number-button`}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                autocomplete.forEach((item: IBusinessSMSNumbersContent) =>
                  arrayHelper.push(item)
                );
                setAutocomplete([]);
              }}
              disableElevation
            >
              Add
            </Button>
            <Table className={classes.table} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell valign="baseline">Number</TableCell>
                  <TableCell valign="baseline">Destination Name</TableCell>
                  <TableCell align="right" valign="baseline" width="100">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(businessSMSNumbers.state === 'loading' ||
                  !formik.values.numbers) && (
                  <TableRow>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                    <TableCell>
                      <DefaultActionSkeleton />
                    </TableCell>
                  </TableRow>
                )}
                {businessSMSNumbers.state !== 'loading' &&
                  numbersMemo?.map?.(
                    (num: IBusinessSMSNumbersContent, index: number) => (
                      <TableRow key={num?.numberId}>
                        <TableCell
                          data-pup={`number-did-${index}`}
                          valign="baseline"
                        >
                          {num?.DID ?? ''}
                        </TableCell>
                        <TableCell
                          data-pup={`number-name-${index}`}
                          valign="baseline"
                        >
                          {num?.destinationName ?? ''}
                        </TableCell>
                        <TableCell
                          align="right"
                          valign="baseline"
                          padding="none"
                        >
                          <IconButton
                            data-pup={`delete-business-sms-number-button-${index}`}
                            aria-label="delete"
                            onClick={(event) => {
                              arrayHelper.remove(index);
                            }}
                            className={classes.deleteIconButton}
                            size="large"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  )}
              </TableBody>
            </Table>
          </>
        )}
      />
    </>
  );
};
